import cn from 'classnames';
import { useStateList, useToggle } from 'react-use';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { Carousel, Collapse } from '@ui';
import { StatsUrl } from '@icons';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';
import { convertMediaFiles, formatClinicalTime } from '@features/components/ClinicalData/utils';
import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import {
  ClinicalGalleryItem,
  ClinicalNavigation,
  ClinicalSection,
  RadiometerContentGrid,
  TimeStepper,
} from '@features/components/ClinicalData/components';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { formatRadiometerData } from '@features/components/ClinicalData/sections/utils';
import { ClinicalData, OrganType } from '@/api/apis/case';
import { getColorsByOrganType } from '@/utils/helpers';
import { useRowScroll } from '@/hooks/useRowScroll';

type RadiometerSectionProps = {
  organType: OrganType;
  clinicalData: ClinicalData;
};

const label = 'Radiometer';

export const RadiometerSection = ({ organType, clinicalData }: RadiometerSectionProps) => {
  const colors = getColorsByOrganType(organType);
  const items = orderBy(formatRadiometerData(clinicalData, organType), 'timestamp');
  const { state, prev, next, currentIndex, isFirst, isLast } = useStateList(items);
  const { listContainerRef, listItemRefs, onNavigationPrev, onNavigationNext, maxLeft, maxRight } = useRowScroll();
  const [expanded, toggleExpanded] = useToggle(false);
  const mediaList = useMemo(
    () =>
      items.flatMap((item) =>
        item.mediaFiles ? convertMediaFiles(item.mediaFiles, item.timestamp, item.timeZone, item.ocsTime) : [],
      ),
    [items],
  );
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();

  return (
    <>
      <ClinicalSection
        color={colors.bg40}
        className="row-span-1"
        header={label}
        icon={StatsUrl}
        endContent={
          !!items.length && (
            <TimeStepper
              ocsTime={state?.ocsTime}
              timezone={state?.timeZone}
              timestamp={state?.timestamp}
              isFirst={isFirst}
              isLast={isLast}
              onNext={next}
              onPrev={prev}
            />
          )
        }
      >
        {items.length > 0 && (
          <div className="flex flex-col gap-3 w-full overflow-hidden">
            <Carousel currentIndex={currentIndex} itemClassName="w-full">
              {items?.map((item) => (
                <div key={item.timestamp} className="flex flex-col gap-3">
                  <RadiometerContentGrid key={item.timestamp} stats={item.stats} />
                  <div className="flex flex-col rounded-md border border-base-40">
                    <button className="flex items-center gap-1 p-2" onClick={toggleExpanded}>
                      <MdKeyboardArrowRight size={20} className={cn({ 'rotate-90': expanded })} />
                      <span className="text-s-m text-base-10">Photo Gallery of Radiometer</span>
                    </button>
                    <Collapse isExpanded={expanded} className={`flex flex-col gap-2 p-3 scroll ${organType}`}>
                      <div
                        className="grid grid-flow-col auto-cols-max gap-2 relative overflow-auto pb-1"
                        ref={listContainerRef}
                      >
                        {item.mediaFiles?.map((f, i) => (
                          <ClinicalGalleryItem
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            ref={(el) => {
                              listItemRefs.current[i] = el;
                            }}
                            topLabel={item.ocsTime}
                            bottomLabel={formatClinicalTime(item.timestamp, item.timeZone)}
                            file={f}
                            onClick={() => handlePreviewOpen(f.fileId)}
                          />
                        ))}
                      </div>

                      <ClinicalNavigation
                        label={`${item.mediaFiles?.length} Attachments`}
                        prev={onNavigationPrev}
                        next={onNavigationNext}
                        prevDisabled={maxLeft}
                        nextDisabled={maxRight}
                      />
                    </Collapse>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        )}
      </ClinicalSection>
      {isPreviewOpen && (
        <MediaPreview
          heading={label}
          media={mediaList}
          onClose={handlePreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
        />
      )}
    </>
  );
};
